import type { SuperHubDocumentData } from '@core/store/SuperHub/Document';
import type { ReadReferenceType } from '@readme/api/mappings/page/reference/types';
import type { OASDocument } from 'oas/types';
import type { PropsWithChildren } from 'react';

import Oas from 'oas';
import { createContext, useContext, useState, useEffect, useMemo } from 'react';

import { useSuperHubStore } from '@core/store';

interface CurrentPageData {
  doc?: {
    title?: string;
    slug?: string;
    body?: string;
    api?: {
      method?: string;
      path?: string;
      url?: string;
    };
    swagger?: {
      path?: string;
    };
  };
  oasDefinition?: OASDocument;
}

export interface PageContext {
  content?: string;
  slug?: string;
  title?: string;
}

interface CurrentPageContextValue {
  pageContext: PageContext | undefined;
  setCurrentPage: (data: CurrentPageData) => void;
}

const CurrentPageContext = createContext<CurrentPageContextValue | undefined>(undefined);

function extractReferenceContent(doc: ReadReferenceType['data']): string {
  const parts: string[] = [];

  if ('content' in doc && doc.content && 'body' in doc.content && doc.content.body) {
    parts.push('# Page Content\n');
    parts.push(doc.content.body);
    parts.push('\n---\n');
  }

  if (doc.api?.schema) {
    if (doc.api.schema.info) {
      parts.push('# API Information\n');
      if (doc.api.schema.info.title) parts.push(`**Title:** ${doc.api.schema.info.title}`);
      if (doc.api.schema.info.version) parts.push(`**Version:** ${doc.api.schema.info.version}`);
      if (doc.api.schema.info.description) parts.push(`\n${doc.api.schema.info.description}`);
      parts.push('\n---\n');
    }

    if (doc.api.method && doc.api.schema.paths && doc.api.path) {
      const pathItem = doc.api.schema.paths[doc.api.path];
      if (pathItem) {
        const operation = pathItem[doc.api.method.toLowerCase()];
        if (operation) {
          parts.push(`# ${doc.api.method.toUpperCase()} ${doc.api.path}\n`);

          if (operation.summary) parts.push(`**Summary:** ${operation.summary}\n`);
          if (operation.description) parts.push(`${operation.description}\n`);

          if (operation.parameters && operation.parameters.length > 0) {
            parts.push('\n## Parameters\n');
            operation.parameters.forEach(
              (param: {
                name?: string;
                description?: string;
                in?: string;
                required?: boolean;
                schema?: { type?: string };
              }) => {
                if (param.name) {
                  const required = param.required ? ' (required)' : '';
                  const location = param.in ? ` [${param.in}]` : '';
                  const type = param.schema?.type ? ` - \`${param.schema.type}\`` : '';
                  parts.push(`- **${param.name}**${location}${type}${required}`);
                  if (param.description) {
                    parts.push(`  ${param.description}`);
                  }
                }
              },
            );
          }

          if (operation.requestBody) {
            const requestBody = operation.requestBody as {
              description?: string;
              required?: boolean;
              content?: Record<string, { schema?: unknown }>;
            };
            parts.push('\n## Request Body\n');
            if (requestBody.description) parts.push(requestBody.description);
            if (requestBody.required) parts.push('(Required)');
            if (requestBody.content) {
              const contentTypes = Object.keys(requestBody.content);
              if (contentTypes.length > 0) {
                parts.push(`\nContent Types: ${contentTypes.join(', ')}`);
              }
            }
          }

          if (operation.responses) {
            parts.push('\n## Responses\n');
            Object.entries(operation.responses).forEach(entry => {
              const [code, response] = entry;
              const responseObj = response as {
                description?: string;
                content?: Record<string, { schema?: unknown }>;
              };
              if (responseObj.description) {
                parts.push(`### ${code}\n${responseObj.description}`);
                if (responseObj.content) {
                  const contentTypes = Object.keys(responseObj.content);
                  if (contentTypes.length > 0) {
                    parts.push(`Content Types: ${contentTypes.join(', ')}`);
                  }
                }
                parts.push('');
              }
            });
          }
        }
      }
    }

    if (doc.api.method && doc.api.schema.paths && doc.api.path) {
      const pathItem = doc.api.schema.paths[doc.api.path];
      if (pathItem) {
        const operation = pathItem[doc.api.method.toLowerCase()];
        if (operation) {
          if (operation.requestBody) {
            const requestBody = operation.requestBody as {
              content?: Record<string, { schema?: unknown }>;
            };
            if (requestBody.content) {
              parts.push('\n---\n## Request Schema\n');
              Object.entries(requestBody.content).forEach(([contentType, content]) => {
                if (content.schema) {
                  parts.push(`\n### ${contentType}\n`);
                  parts.push('```json');
                  parts.push(JSON.stringify(content.schema, null, 2));
                  parts.push('```');
                }
              });
            }
          }

          if (operation.responses) {
            parts.push('\n---\n## Response Schemas\n');
            Object.entries(operation.responses).forEach(entry => {
              const [code, response] = entry;
              const responseObj = response as {
                content?: Record<string, { schema?: unknown }>;
              };
              if (responseObj.content) {
                Object.entries(responseObj.content).forEach(([contentType, content]) => {
                  if (content.schema) {
                    parts.push(`\n### ${code} - ${contentType}\n`);
                    parts.push('```json');
                    parts.push(JSON.stringify(content.schema, null, 2));
                    parts.push('```');
                  }
                });
              }
            });
          }
        }
      }
    }

    if (doc.api.schema.info || doc.api.schema.tags) {
      parts.push('\n---\n## API Metadata\n');
      if (doc.api.schema.info) {
        parts.push('```json');
        parts.push(
          JSON.stringify(
            {
              info: doc.api.schema.info,
              servers: doc.api.schema.servers,
            },
            null,
            2,
          ),
        );
        parts.push('```');
      }
    }
  }

  return parts.join('\n');
}

function extractSuperHubPageContext(doc: SuperHubDocumentData | null): PageContext | undefined {
  if (!doc) return undefined;

  let content = '';

  if ('api' in doc && doc.api) {
    content = extractReferenceContent(doc as ReadReferenceType['data']);
  } else if ('content' in doc && doc.content && 'body' in doc.content && doc.content.body) {
    content = doc.content.body;
  }

  return {
    title: doc.title ?? undefined,
    slug: doc.slug,
    content,
  };
}

export function CurrentPageProvider({ children }: PropsWithChildren) {
  const [currentPage, setCurrentPage] = useState<CurrentPageData>({});
  const [dereferencedSchema, setDereferencedSchema] = useState<unknown>(null);
  const [isSuperHub, superHubDoc] = useSuperHubStore(s => [s.isSuperHub, s.document.data]);

  const { doc, oasDefinition } = currentPage;

  // Load the OAS schema when it changes
  useEffect(() => {
    if (oasDefinition && doc?.api) {
      try {
        const oas = new Oas(oasDefinition);
        setDereferencedSchema(oas.api);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load OAS schema:', err);
        setDereferencedSchema(oasDefinition);
      }
    } else {
      setDereferencedSchema(null);
    }
  }, [oasDefinition, doc?.api]);

  const pageContext = useMemo(() => {
    if (isSuperHub && superHubDoc) {
      return extractSuperHubPageContext(superHubDoc);
    }

    if (doc) {
      // For API reference pages, extract content using the OAS schema
      if (doc.api && dereferencedSchema) {
        const normalizedPath = doc.api.url || doc.api.path || doc.swagger?.path;
        const referenceDoc = {
          api: {
            ...doc.api,
            path: normalizedPath,
            schema: dereferencedSchema,
          },
          content: { body: doc.body || '' },
        } as ReadReferenceType['data'];

        const content = extractReferenceContent(referenceDoc);

        return {
          title: doc.title,
          slug: doc.slug,
          content,
        } as PageContext;
      }

      // For regular pages (guides, custom pages), just the body
      return {
        title: doc.title,
        slug: doc.slug,
        content: doc.body || '',
      } as PageContext;
    }

    return undefined;
  }, [isSuperHub, superHubDoc, doc, dereferencedSchema]);

  return <CurrentPageContext.Provider value={{ pageContext, setCurrentPage }}>{children}</CurrentPageContext.Provider>;
}

export function useCurrentPage() {
  return useContext(CurrentPageContext);
}

export function usePageContext(): PageContext | undefined {
  return useCurrentPage()?.pageContext;
}
